'use client';

import React, { useState, useEffect, useRef } from 'react';
import type { Metadata } from 'next';
import { FixedLayout } from '@/components/ui/layout/fixed-layout';
import Image from 'next/image';
import { ArrowUpRight, Copy, Check, ExternalLink } from 'lucide-react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { OverlayText } from '@/components/ui/common/overlay-text';
import type { About, Media, Datablockchain } from '@/types';
import { getMediaData } from '@/lib/utils/media.utils';
import { lexicalStateToHtml, lexicalStateToPlainText } from '@/lib/utils/lexical-to-html';
import { useTenant } from '@/contexts/TenantContext';
import Link from 'next/link';

type Stats = {
  artworks: number;
  collections: number;
  oneofones: number;
  exhibitions: number;
  awards: number;
  soloExhibitions: number;
  groupExhibitions: number;
  /** Everyone who has ever collected a work. */
  collectors: number;
  /** Wallets currently holding one. */
  currentCollectors: number;
};

/**
 * Substitutes {{token}} placeholders in the biography with live counts.
 *
 * The biography used to state the exhibition record as prose written by hand,
 * which meant it drifted the moment a show was added: it claimed twelve solo
 * exhibitions and over twenty group shows while the CMS held thirteen and
 * twenty-five. Tokens keep one source of truth, so the sentence ages with the
 * work rather than against it. An unknown token is left visible on purpose,
 * because a stray {{typo}} in the copy is easier to notice than a silent gap.
 */
function applyStatTokens(html: string, stats: Stats | null): string {
  if (!stats) return html;

  const values: Record<string, number> = {
    artworks: stats.artworks,
    collections: stats.collections,
    oneofones: stats.oneofones,
    exhibitions: stats.exhibitions,
    awards: stats.awards,
    soloExhibitions: stats.soloExhibitions,
    groupExhibitions: stats.groupExhibitions,
    collectors: stats.collectors,
    currentCollectors: stats.currentCollectors,
  };

  return html.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, key: string) =>
    key in values ? String(values[key]) : match,
  );
}

// Animation variants for staggered fade-in
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

// Container variant for staggered children
const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.2,
    },
  },
};

// Hook to animate when element comes into view
function Section({
  children,
  className,
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
      variants={fadeInUp}
      transition={{ delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// Animated section title component with text reveal effect
function AnimatedTitle({
  children,
  className,
  marginClass = 'mb-8',
}: {
  children: React.ReactNode;
  className?: string;
  marginClass?: string;
}) {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, amount: 0.5 });

  return (
    <div ref={containerRef} className={`relative uppercase text-[10px] font-normal ${marginClass} ${className || ''}`}>
      <div className="relative overflow-hidden inline-block">
        {children}
        <motion.div
          className="absolute inset-0 bg-white"
          initial={{ left: 0, right: 'unset', width: '100%' }}
          animate={
            isInView
              ? {
                  left: '100%',
                  right: 0,
                  width: 0,
                  transition: {
                    duration: 0.6,
                    ease: [0.22, 1, 0.36, 1],
                    delay: 0.1,
                  },
                }
              : { left: 0, right: 'unset', width: '100%' }
          }
        />
      </div>
    </div>
  );
}

export default function AboutClientComponent({
  aboutData,
  metadata,
  stats,
}: {
  aboutData: About | null;
  metadata: Metadata;
  stats: Stats | null;
}) {
  const { isOrkhan, artistName } = useTenant();
  const [copiedAddress, setCopiedAddress] = useState<string | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHovering, _setIsHovering] = useState(false);
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    biography: true,
    artistStatement: false,
  });

  const biographyPreview =
    (aboutData?.artistBiography ? applyStatTokens(lexicalStateToPlainText(aboutData.artistBiography), stats).trim() : '') ||
    (isOrkhan
      ? 'Orkhan Mammadov (b. 1990, Ganja, Azerbaijan) is an internationally recognized new media artist whose practice explores the intersections of art, science, and technology...'
      : '');

  const statementPreview =
    (aboutData?.artistStatement ? lexicalStateToPlainText(aboutData.artistStatement).trim() : '') ||
    (isOrkhan
      ? 'Inspired by the transformative power of data and machine intelligence, my work resides at the intersection of art, technology, and collective memory...'
      : '');

  const toggleSection = (key: string) => {
    setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Image URLs - filter out empty/undefined values

  let imageUrls: string[] = [];

  if (aboutData?.aboutImages && Array.isArray(aboutData.aboutImages)) {
    imageUrls = aboutData.aboutImages
      .map((image: number | Media) => getMediaData(image).url)
      .filter((url): url is string => !!url && url !== '');
  }

  // Auto-cycle images on hover
  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (isHovering) {
      interval = setInterval(() => {
        setCurrentImageIndex(prevIndex => (prevIndex + 1) % imageUrls.length);
      }, 2000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isHovering, imageUrls.length]);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedAddress(text);
    setTimeout(() => setCopiedAddress(null), 2000);
  };

  const studioImage = (
    <div>
      <AnimatedTitle marginClass="mb-0">[ HELLO WORLD ]</AnimatedTitle>
      <div
        id="studio-images"
        onClick={e => {
          const rect = e.currentTarget.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const width = rect.width;

          if (x < width / 2) {
            setCurrentImageIndex(prevIndex => (prevIndex === 0 ? imageUrls.length - 1 : prevIndex - 1));
          } else {
            setCurrentImageIndex(prevIndex => (prevIndex + 1) % imageUrls.length);
          }
        }}
        className="relative overflow-hidden cursor-pointer"
      >
        <Image
          src={imageUrls[currentImageIndex] || ''}
          alt={`${artistName} Studio`}
          width={400}
          height={420}
          className="object-cover grayscale hover:grayscale-0 transition-all duration-300 ease-in-out aspect-square rounded-md"
          style={{
            transform: 'translateZ(0)',
            transition: 'all 0.3s ease-in-out',
          }}
        />

        {/* Image indicators */}
        <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2">
          {imageUrls.map((_, index) => (
            <div
              key={index}
              style={{
                width: (400 / imageUrls.length) * 0.8,
                height: '0.5px',
                backgroundColor: index === currentImageIndex ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.45)',
                transition: 'all 0.3s ease-in-out',
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );

  interface MintingAddress {
    walletAddress?: string | null;
    blockchains?: (number | null) | Datablockchain;
    id?: string | null;
  }

  const OFFICIAL_ADDRESSES: MintingAddress[] =
    aboutData?.officialMintingAddresses?.map((address: MintingAddress) => ({
      walletAddress: address.walletAddress,
      blockchains: address.blockchains,
    })) || [];

  return (
    <FixedLayout breadcrumbs={[{ label: 'ABOUT', isActive: true }]}>
      <div className="w-full h-full p-[15px] font-mono text-[10px]">
        <div className="relative flex flex-col md:flex-row h-full">
          {/* Main scrollable content */}
          <div className="flex-1 h-[calc(100dvh-107px)] overflow-y-auto pr-0 md:pr-5 scrollbar-hide relative">
            {/* Add CSS to hide scrollbar but maintain functionality */}
            <style jsx global>{`
              .scrollbar-hide {
                -ms-overflow-style: none; /* IE and Edge */
                scrollbar-width: none; /* Firefox */
              }
              .scrollbar-hide::-webkit-scrollbar {
                display: none; /* Chrome, Safari, Opera */
              }
            `}</style>
            {/* Header */}
            <motion.div
              className="mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            >
              <h1 className="text-[26px] md:text-[28px] lg:text-[30px] uppercase font-superior leading-tight tracking-tight text-black">
                ABOUT
              </h1>
              {aboutData?.description && (
                <p className="text-[11px] font-mono uppercase tracking-wide text-zinc-500 mt-4">
                  {aboutData.description}
                </p>
              )}
            </motion.div>

            <div className="block lg:hidden absolute lg:static bottom-0 right-0 z-[1]">{studioImage}</div>

            {/* Content column */}
            <div className="max-w-full relative z-[2] mt-[80dvh] lg:mt-0">
              {/* Biography: collapsible, 2-column */}
              <div className="mb-10 mt-0 lg:mt-[calc(100dvh-600px)] pt-6 lg:pt-0 cursor-pointer group" onClick={() => toggleSection('biography')}>
                <div
                  className="w-full flex items-center justify-between"
                >
                  <AnimatedTitle marginClass="mb-0">[ BIOGRAPHY ]</AnimatedTitle>
                  <span
                    className="text-[10px] text-zinc-400 group-hover:text-black transition-colors duration-300"
                    style={{
                      display: 'inline-block',
                      transform: openSections.biography ? 'rotate(90deg)' : 'rotate(0deg)',
                      transition: 'transform 0.3s ease',
                    }}
                  >
                    →
                  </span>
                </div>
                {!openSections.biography && biographyPreview && (
                  <p className="text-[12px] font-geist text-zinc-400 mt-3 line-clamp-1">
                    {biographyPreview}
                  </p>
                )}
                <div
                  className="overflow-hidden transition-all duration-500 ease-in-out"
                  style={{
                    maxHeight: openSections.biography ? '2000px' : '0',
                    opacity: openSections.biography ? 1 : 0,
                    marginTop: openSections.biography ? '24px' : '0',
                  }}
                >
                  <div
                    className="text-[14px] leading-[1.7] font-geist text-zinc-600 [&>p]:mb-3"
                    style={{ columnCount: 2, columnGap: '2rem' }}
                    dangerouslySetInnerHTML={{
                      __html: aboutData?.artistBiography
                        ? applyStatTokens(lexicalStateToHtml(aboutData.artistBiography), stats)
                        : '',
                    }}
                  />
                </div>
              </div>

              {/* Artist Statement: collapsible, 2-column */}
              <div className="mb-10 cursor-pointer group" onClick={() => toggleSection('artistStatement')}>
                <div className="w-full flex items-center justify-between">
                  <AnimatedTitle marginClass="mb-0">[ ARTIST STATEMENT ]</AnimatedTitle>
                  <span
                    className="text-[10px] text-zinc-400 group-hover:text-black transition-colors duration-300"
                    style={{
                      display: 'inline-block',
                      transform: openSections.artistStatement ? 'rotate(90deg)' : 'rotate(0deg)',
                      transition: 'transform 0.3s ease',
                    }}
                  >
                    →
                  </span>
                </div>
                {!openSections.artistStatement && statementPreview && (
                  <p className="text-[12px] font-geist text-zinc-400 mt-3 line-clamp-1">
                    {statementPreview}
                  </p>
                )}
                <div
                  className="overflow-hidden transition-all duration-500 ease-in-out"
                  style={{
                    maxHeight: openSections.artistStatement ? '2000px' : '0',
                    opacity: openSections.artistStatement ? 1 : 0,
                    marginTop: openSections.artistStatement ? '24px' : '0',
                  }}
                >
                  <div
                    className="text-[14px] leading-[1.7] font-geist text-zinc-600 [&>p]:mb-3"
                    style={{ columnCount: 2, columnGap: '2rem' }}
                    dangerouslySetInnerHTML={{
                      __html: aboutData?.artistStatement
                        ? lexicalStateToHtml(aboutData.artistStatement)
                        : '',
                    }}
                  />
                </div>
              </div>

              <Section className="mb-16 mt-[150px]" delay={0.2}>
                <AnimatedTitle>[ HIGHLIGHTS ]</AnimatedTitle>

                <div className="grid grid-cols-2 xl:grid-cols-5 gap-5">
                  <div className="flex flex-col items-start p-4 group bg-zinc-100 hover:bg-zinc-200 transition-all duration-300 cursor-default  rounded-lg relative">
                    <p className="text-3xl md:text-4xl font-normal mb-3 font-superior tracking-tight">
                      {stats?.artworks ?? 0}
                    </p>
                    <div className="w-6 h-[1px] bg-black/40 mb-3"></div>
                    <p className="uppercase text-[8px] tracking-wider">ARTWORKS</p>
                    <Link href="/catalog" className="absolute bottom-4 right-4 z-10 text-black hover:text-black/70 transition-colors duration-300">
                      <ExternalLink size={16} />
                    </Link>
                  </div>

                  <div className="flex flex-col items-start p-4 group bg-zinc-100 hover:bg-zinc-200 transition-all duration-300 cursor-default  rounded-lg relative">
                    <p className="text-3xl md:text-4xl font-normal mb-3 font-superior tracking-tight">
                      {stats?.collections ?? 0}
                    </p>
                    <div className="w-6 h-[1px] bg-black/40 mb-3"></div>
                    <p className="uppercase text-[8px] tracking-wider">COLLECTIONS</p>
                    <Link href="/catalog/collections" className="absolute bottom-4 right-4 z-10 text-black hover:text-black/70 transition-colors duration-300">
                      <ExternalLink size={16} />
                    </Link>
                  </div>

                  <div className="flex flex-col items-start p-4 group bg-zinc-100 hover:bg-zinc-200 transition-all duration-300 cursor-default  rounded-lg relative">
                    <p className="text-3xl md:text-4xl font-normal mb-3 font-superior tracking-tight">
                      {stats?.oneofones ?? 0}
                    </p>
                    <div className="w-6 h-[1px] bg-black/40 mb-3"></div>
                    <p className="uppercase text-[8px] tracking-wider">ONE OF ONES</p>
                    <Link href="/catalog/one-of-ones" className="absolute bottom-4 right-4 z-10 text-black hover:text-black/70 transition-colors duration-300">
                      <ExternalLink size={16} />
                    </Link>
                  </div>

                  <div className="flex flex-col items-start p-4 group bg-zinc-100 hover:bg-zinc-200 transition-all duration-300 cursor-default  rounded-lg relative">
                    <p className="text-3xl md:text-4xl font-normal mb-3 font-superior tracking-tight">
                      {stats?.exhibitions ?? 0}
                    </p>
                    <div className="w-6 h-[1px] bg-black/40 mb-3"></div>
                    <p className="uppercase text-[8px] tracking-wider">EXHIBITIONS</p>
                    <Link href="/catalog/exhibitions" className="absolute bottom-4 right-4 z-10 text-black hover:text-black/70 transition-colors duration-300">
                      <ExternalLink size={16} />
                    </Link>
                  </div>

                  <div className="flex flex-col items-start p-4 group bg-zinc-100 hover:bg-zinc-200 transition-all duration-300 cursor-default  rounded-lg relative">
                    <p className="text-3xl md:text-4xl font-normal mb-3 font-superior tracking-tight">{stats?.awards ?? 0}</p>
                    <div className="w-6 h-[1px] bg-black/40 mb-3"></div>
                    <p className="uppercase text-[8px] tracking-wider">AWARDS</p>
                  </div>
                </div>
              </Section>

              <Section className="mb-16 mt-[150px]" delay={0.2}>
                <AnimatedTitle className={''}>[ OFFICIAL MINTING ADDRESSES ]</AnimatedTitle>

                <div className="space-y-5">
                  {OFFICIAL_ADDRESSES.map((item: MintingAddress, index: number) => (
                    <div key={index} className="bg-zinc-100 rounded-lg p-4">
                      <div className="flex items-start justify-between flex-col md:flex-row gap-2">
                        <div className="flex items-center  gap-2">
                          <div className="bg-black text-white text-[8px] px-2 py-1 rounded uppercase font-bold tracking-wider">
                            {item.blockchains && typeof item.blockchains !== 'number' && (
                              <h3 className="font-medium">{item.blockchains.symbol}</h3>
                            )}
                          </div>
                          <p className="text-[8px] uppercase font-bold">
                            {index === 0 ? 'PRIMARY MINT ADDRESS' : 'SECONDARY MINT ADDRESS'}
                          </p>
                        </div>
                        <button
                          onClick={() => copyToClipboard(item.walletAddress || '')}
                          className="flex items-center gap-1 bg-white px-2 py-1 rounded border border-zinc-200 hover:bg-zinc-50 transition-colors"
                        >
                          <p className="text-[9px] font-medium font-mono tracking-tight">{item.walletAddress}</p>
                          {copiedAddress === item.walletAddress ? (
                            <Check className="h-3 w-3 text-zinc-600" />
                          ) : (
                            <Copy className="h-3 w-3 text-zinc-400" />
                          )}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </Section>

            </div>
          </div>

          {/* Fixed right column with image */}
          <div className="hidden lg:block w-[400px] shrink-0">
            <div className="static lg:sticky top-0 right-0 lg:h-[calc(100dvh-107px)] flex items-end justify-end">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{
                  duration: 1.2,
                  ease: [0.22, 1, 0.36, 1],
                  delay: 0.3,
                }}
              >
                {studioImage}
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </FixedLayout>
  );
}
